import { useState, useEffect, useRef, useCallback } from "react";
import workflowService from "../services/workflowService";

export default function useWorkflow(pipelineId) {

    const [workflow, setWorkflow] = useState(null);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const [selectedStage, setSelectedStage] = useState(null);

    const [selectedNode, setSelectedNode] = useState(null);
    const selectedStageRef = useRef(null);
    const selectedNodeRef = useRef(null);

    useEffect(() => {
        selectedStageRef.current = selectedStage;
    }, [selectedStage]);

    useEffect(() => {
        selectedNodeRef.current = selectedNode;
    }, [selectedNode]);

    /*
    |--------------------------------------------------------------------------
    | Load Workflow
    |--------------------------------------------------------------------------
    */

    const refreshWorkflow = useCallback(async () => {

        if (!pipelineId) {
            setWorkflow(null);
            setError("Pipeline ID is missing.");
            return;
        }

        setLoading(true);
        setError("");

        try {

            const response = await workflowService.getWorkflow(pipelineId);

            // Safely retrieve workflow data whether response has a `.data` wrapper or direct object
            const workflowData = response?.data ?? response;

            if (workflowData) {

                setWorkflow(workflowData);

                if (workflowData?.stages?.length) {

                    const stage =
                        workflowData.stages.find(
                            s => s.id === selectedStageRef.current?.id
                        ) ??
                        workflowData.stages[0];

                    setSelectedStage(stage);

                    const node =
                        stage.nodes?.find(
                            n => n.id === selectedNodeRef.current?.id
                        ) ?? null;

                    setSelectedNode(node);

                } else {
                    setSelectedStage(null);
                    setSelectedNode(null);
                }

            }

        } catch (error) {

            console.error("Failed to load workflow", error);
            setWorkflow(null);
            setSelectedStage(null);
            setSelectedNode(null);
            setError(
                error?.response?.data?.message ||
                "Unable to load this pipeline. Refresh the page or return to pipelines."
            );

        } finally {

            setLoading(false);

        }

    }, [pipelineId]);

    /*
    |--------------------------------------------------------------------------
    | Initial Load & Auto-Refresh Listener
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (pipelineId) {
            localStorage.setItem("selectedPipelineId", pipelineId);
        }

        refreshWorkflow();

        // Listen for automatic refresh triggers from workflowService
        const handleAutoRefresh = () => {
            refreshWorkflow();
        };

        window.addEventListener("workflow:refresh", handleAutoRefresh);

        return () => {
            window.removeEventListener("workflow:refresh", handleAutoRefresh);
        };
    }, [pipelineId, refreshWorkflow]);

    /*
    |--------------------------------------------------------------------------
    | Selection
    |--------------------------------------------------------------------------
    */

    const selectStage = useCallback((stage) => {

        setSelectedStage(stage);

        setSelectedNode(null);

    }, []);

    const selectNode = useCallback((node) => {

        setSelectedNode(node);

    }, []);

    return {

        workflow,

        pipelineId,

        loading,

        error,

        selectedStage,

        selectedNode,

        refreshWorkflow,

        selectStage,

        selectNode,

        setWorkflow,

        setSelectedStage,

        setSelectedNode,

    };

}
